import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

import {
    ICellRendererParams,
    IRowDragItem,
    CellKeyDownEvent,
    RowGroupOpenedEvent,
    FirstDataRenderedEvent,
    RowDragMoveEvent,
    IRowNode,
    RowDragLeaveEvent,
    RowDragEndEvent,
    SelectionChangedEvent,
    CellClassParams,
    ProcessUnpinnedColumnsParams,
    RangeSelectionChangedEvent,
    ColDef,
    CellFocusedEvent,
    CellContextMenuEvent,
    SuppressKeyboardEventParams,
    ValueGetterParams
} from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';

import { SectionApiResponseType, CurrentDropNodeType, UpdateSectionOrderTypes } from '../../queries/apiTypes';
import {
    addMsg,
    deleteMsg,
    duplicateMsg,
    hideMsg,
    HIGHLIGHT_MSG,
    KIT_CREATED_SUCCESS,
    OPTION_APPROVED_MSG,
    OPTION_CREATE_MSG,
    REMOVE_PRODUCT_FROM_KIT,
    reorderMsg,
    SECTION_CREATE_MSG,
    SECTION_NAME_EMPTY_MSG,
    SECTION_UPDATE_MSG,
    someThingWentWrongMsg
} from 'src/features/shared/constants';
import {
    useAddBomDataMutation,
    useApproveProductOption,
    useCreateKitMutation,
    useCreateOptionSectionMutation,
    useDuplicateBomDataMutation,
    useGetBomRoundingPresetQuery,
    useHighLightBomDataMutation,
    useReorderBomDataMutation,
    useToggleHideBomDataMutation,
    useUpdateBomDataMutation,
    useUpdateSectionOrderMutation
} from '../../queries';
import { BooleanObjType, OperationType, PricingDataType, SectionAndBomItemData, SelectedData } from '../types';
import { useLotMap } from '../hooks';
import useDeleteBods from '../hooks/useDeleteBodsHook';
import { BOM_TABLES_ENUM, KIT_ERROR_TYPE, ROW_HEIGHT, ROW_LEVEL, SECTION_TYPE, SECTION_TYPE_FOR_PAYLOAD, TAB_VALUES, AUTO_SELECT_CLASSESS } from '../constants';
import useGetSelectedDataInfo from './useGetSelectedDataInfoHook';
import {
    formatSelectedItemsText,
    getIsLastNodeInSectionHierarchy,
    getTableHeight,
    setPotentialParentRowDragAndDropNode,
    isHighlightDropLine,
    getSelectedSectionIds,
    handleOnRangeSelectionChanged,
    handleRightClickContextMenuChange,
    clearCheckBoxSelectionFromAPI,
    addNewRowsToTheTable,
    getFormattedManufacturerName
} from '../utils';
import { useFocusOnNewRow } from './useFocusOnNewRow';
import useUpdateSectionName from './useUpdateSectionNameHook';
import useCreateSectionHook from './useCreateSectionHook';
import useFocusOnSectionCreate from './useFocusOnFirstSectionOnCreate';
import { getAutoGroupColumnDef } from '../../BillOfMaterials/helper';
import { useHideUnhideStatus } from './useHideUnhideStatus';
import { HandleKitPricingParamsType } from './useKitPricingUpdate';
import { useHighlightProducts } from './useHighLightProducts';
import { useCreateNewBods } from './useCreateNewBodsOnCopyPaste';
import { useCompanyMfgQuery } from 'src/features/Settings/Manufacturer/queries';
import { BulkEditPayload } from '../../pricing&LeadTime/bulkEdit/bulkEditPopup/queries/apiTypes';

interface IBomTableActionHandler {
    bomId: string;
    isOptionTable: boolean;
    tableRef: MutableRefObject<AgGridReact<any> | null>;
    optionSection?: SectionApiResponseType[];
    getSectionBodItems?: (sectionId: number, isOptionTable?: boolean) => SectionAndBomItemData[];
    tableData: SectionAndBomItemData[];
    tableMaxHeight?: number;
    mainProductOptionsInfo?: Record<number, number[]>;
    currentGroupWhilePastingRef: MutableRefObject<number | null>;
    selectedRowsRef: MutableRefObject<Set<number>>;
    aggregateSelectedItemsFromSections?: (data: SelectedData[], kitId?: number) => void;
    onPricingInfoEdit: (info: PricingDataType) => Promise<void>;
    handleKitPricingUpdate: ({ onPricingInfoEdit, type, tableRef, productIds }: HandleKitPricingParamsType) => Promise<void>;
    currentTabValue: string | null;
}

const PINNED_AND_FIX_COLUMN_WIDTH = 400;

export const useBomTableActionHandlers = ({
    bomId = '',
    isOptionTable = false,
    tableRef,
    optionSection = [],
    getSectionBodItems = () => [],
    tableData = [],
    tableMaxHeight = 300,
    mainProductOptionsInfo = {},
    currentGroupWhilePastingRef,
    selectedRowsRef,
    aggregateSelectedItemsFromSections,
    onPricingInfoEdit = async () => {},
    handleKitPricingUpdate,
    currentTabValue
}: IBomTableActionHandler) => {
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isAddOptionModalOpen, setIsAddOptionModalOpen] = useState(false);
    //this is needed because we don't show loader for update bod item but we need it when "Add Option" is used
    const [loadingFlag, setLoadingFlag] = useState(false);
    const [bodInfoForAddOption, setBodInfoForAddOption] = useState<{ type: string; qty?: number; productToReplaceId?: number }>();
    const [isLotSelectedForMovingProducts, setLotSelectedForMovingProducts] = useState(false);
    const [movingIds, setMovingIds] = useState<number[]>([]);
    const [currentSectionId, setCurrentSectionId] = useState<number>();
    const [openMoveDialog, setOpenMoveDialog] = useState(false);
    const [deleteType, setDeleteType] = useState<'bulk' | 'single' | ''>('');
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [openMoveProductDialog, setOpenMoveProductDialog] = useState(false);
    const [currentBodId, setCurrentBodId] = useState<number>();
    const [selectedItems, setSelectedItems] = useState<SelectedData[]>([]);
    const [gridReady, setGridReady] = useState(false);
    const [maxWidthForPinnedColumn, setMaxWidthForPinnedColumn] = useState<number>();
    const [tableHeight, setTableHeight] = useState<number>(317);
    const [kitsExpansionObj, setKitsExpansionObj] = useState<BooleanObjType>({});
    const [kitCreationError, setKitCreationError] = useState<KIT_ERROR_TYPE>();
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [quickFilterText, setQuickFilerText] = useState('');
    const [isContextMenuOpen, setContextMenu] = useState<boolean>(false);
    const [openBulkEditModal, setOpenBulkEditModal] = useState(false);

    const expandedRowsContainerRef = useRef<Array<number>>([]);
    const dragInitialParentNodeRef = useRef<{ id: number; isProduct?: boolean; isKit?: boolean } | null>();
    const currentDropNodeRef = useRef<CurrentDropNodeType | null>(null);
    const potentialParentRef = useRef<IRowNode | null>(null);
    const startColumnOnPasteRef = useRef<string>();

    const { data: companyMfgData, isLoading: companyManufactureLoading } = useCompanyMfgQuery();

    const { mutateAsync: duplicateData, isLoading: duplicateLoading } = useDuplicateBomDataMutation(bomId);
    const { mutateAsync: addData, isLoading: addDataLoading } = useAddBomDataMutation(bomId);
    const { mutateAsync: createOption } = useCreateOptionSectionMutation();
    const { mutateAsync: updateData } = useUpdateBomDataMutation();
    const { mutateAsync: postApproveOption, isLoading: isApproveOptionLoading } = useApproveProductOption();
    const { handleDeleteItems, isLoading: deleteLoading } = useDeleteBods({ isOptionTable, tableRef, onPricingInfoEdit });
    const { mutateAsync: toggleHighLightData, isLoading: highLightLoading } = useHighLightBomDataMutation();
    const { mutateAsync: toggleHideData, isLoading: hideLoading } = useToggleHideBomDataMutation();
    const { mutateAsync: reOrderSectionMutation } = useUpdateSectionOrderMutation();
    const { mutateAsync: reorderNestingData } = useReorderBomDataMutation();
    const { onEditSectionName } = useUpdateSectionName();
    const { onCreateSection: handleCreateSection, isLoading: createSectionLoading } = useCreateSectionHook();
    const { mutateAsync: createKit, isLoading: createKitLoading } = useCreateKitMutation(Number(bomId || 0));

    const lotMap = useLotMap(Number(bomId));
    const hideUnhideResult = useHideUnhideStatus(selectedItems, tableRef);
    const {
        productIds,
        completelySelectedKitIdsFromCompleteSections,
        completelySelectedKitIdsFromPartialSections,
        hierarchy,
        componentIdsFromPartialKits,
        isCreateKitDisabled,
        isChangeSectionDisabled,
        selectedText,
        completelySelectedSectionIds,
        productsIdsFromPartialSections,
        partiallySelectedKitIds,
        componentsFromPartialKits,
        productsFromCompleteSections,
        productsFromPartialSections
    } = useGetSelectedDataInfo({ selectedItems });
    const highlightResult = useHighlightProducts(productsFromCompleteSections, productsFromPartialSections);
    const { addNewRowInEndOfSection } = useFocusOnNewRow<SectionAndBomItemData>(tableRef, tableData, 10);
    const { focusOnFirstSectionCell } = useFocusOnSectionCreate<SectionAndBomItemData>(tableRef, tableData, 10);
    const { createNewRowsFromClipboard, isLoading: isLoadingNewBods } = useCreateNewBods(bomId, onPricingInfoEdit, tableRef);
    const { isLoading: isBomRoundingPresetLoading, data: bomRoundingPreset } = useGetBomRoundingPresetQuery(bomId!, {
        enabled: Boolean(bomId)
    });

    const isTableLoadingAfterMutation = useMemo(
        () =>
            duplicateLoading ||
            addDataLoading ||
            isApproveOptionLoading ||
            deleteLoading ||
            highLightLoading ||
            hideLoading ||
            createSectionLoading ||
            createKitLoading ||
            isLoadingNewBods ||
            companyManufactureLoading ||
            isBomRoundingPresetLoading,
        [
            duplicateLoading,
            addDataLoading,
            isApproveOptionLoading,
            deleteLoading,
            highLightLoading,
            hideLoading,
            createSectionLoading,
            createKitLoading,
            isLoadingNewBods,
            companyManufactureLoading,
            isBomRoundingPresetLoading
        ]
    );

    const isSectionSelected = useMemo(() => {
        if (deleteType === 'bulk') {
            return Boolean(completelySelectedSectionIds?.length);
        }
        return Boolean(currentSectionId) && !deleteIds.length;
    }, [completelySelectedSectionIds?.length, currentSectionId, deleteIds.length, deleteType]);

    const noOfOptionSections = useMemo(() => optionSection?.length || 0, [optionSection?.length]);

    const handleSearch = useCallback((searchString: string) => {
        setQuickFilerText(searchString);
    }, []);

    const isHoverCellClassRules = useMemo(
        () => ({
            'hover-over': (params: CellClassParams) => {
                return params.node === potentialParentRef.current;
            },
            'ag-highlight-drop-bottom-line': (params: CellClassParams) => isHighlightDropLine(params, currentDropNodeRef?.current, true),
            'ag-highlight-drop-top-line': (params: CellClassParams) => isHighlightDropLine(params, currentDropNodeRef?.current, false)
        }),
        []
    );

    const autoGroupColumnDef = useMemo<ColDef>(() => getAutoGroupColumnDef(isHoverCellClassRules), [isHoverCellClassRules]);

    //this function contains api call
    const handleAddOption = useCallback(
        async ({ sectionId, type, qty, productToReplaceId }: { sectionId: number; type?: string; qty?: number; productToReplaceId?: number }) => {
            const sectionBodIds = getSectionBodItems?.(sectionId, true) || [];
            const previousBodId = sectionBodIds.length ? sectionBodIds[sectionBodIds.length - 1].id : null;
            const requestPayload = { section_type: SECTION_TYPE.OPTION, section_id: sectionId, category: type, qty, product_to_replace: productToReplaceId, previous_bod_id: previousBodId };
            await addData({ bomId, input: requestPayload });
            setSnackbarMessage(addMsg);
            setIsAddOptionModalOpen(false);
        },
        [addData, bomId, getSectionBodItems, setIsAddOptionModalOpen, setSnackbarMessage]
    );

    const handleClearSelection = useCallback(() => {
        tableRef.current?.api?.deselectAll();
        setSelectedItems([]);
    }, [tableRef]);

    const onAddBelow = useCallback(
        async (props: ICellRendererParams | CellKeyDownEvent, updateTableKey?: boolean) => {
            const { data, node } = props;
            const { id, groupPath } = data || {};
            const isKitIdNeeded = node?.level === ROW_LEVEL.COMPONENT;
            const item = {
                category: '',
                qty: null,
                manufacturer: '',
                model_number: '',
                previous_bod_id: node.level === ROW_LEVEL.SECTION ? null : id || groupPath[1],
                section_id: groupPath?.[0],
                section_type: isOptionTable ? SECTION_TYPE.OPTION : SECTION_TYPE.MAIN,
                ...(isKitIdNeeded ? { kit_id: groupPath[1] } : {})
            };
            try {
                if (node.level === ROW_LEVEL.COMPONENT) {
                    const kitHideStatus = node?.parent?.data?.is_hidden;
                    await addData({ bomId: bomId!, input: item, is_hidden: kitHideStatus });
                } else {
                    await addData({ bomId: bomId!, input: item });
                }

                setSnackbarMessage(addMsg);
                if (updateTableKey) {
                    handleClearSelection();
                }
                return true;
            } catch (e) {
                setSnackbarMessage(someThingWentWrongMsg);
                return false;
            }
        },
        [addData, bomId, handleClearSelection, isOptionTable]
    );

    const onDuplicate = useCallback(
        async (props: ICellRendererParams) => {
            const { data, node } = props;
            try {
                await duplicateData({
                    bomId: bomId,
                    input: [data.id]
                });

                if (node.level === ROW_LEVEL.COMPONENT) {
                    handleKitPricingUpdate({
                        onPricingInfoEdit,
                        tableRef,
                        type: 'duplicate',
                        productIds: [data.id]
                    });
                }

                setSnackbarMessage(duplicateMsg);
            } catch (error) {
                setSnackbarMessage(someThingWentWrongMsg);
            }
        },
        [bomId, duplicateData, handleKitPricingUpdate, onPricingInfoEdit, tableRef]
    );

    //this function is called when user clicks on "add option" button
    const onAddOption = useCallback(
        async (props: ICellRendererParams) => {
            const { id, category, qty } = props.data;
            const currentType = category?.trim();
            const optionType = currentType ? currentType + ' - Alt' : '';
            const productToReplaceId = id;
            if (noOfOptionSections === 1) {
                const sectionId = optionSection?.[0]?.id || 0;
                await handleAddOption({ type: optionType, qty, productToReplaceId, sectionId });
            } else if (noOfOptionSections === 0) {
                const newOptionSection = await createOption({
                    bomId: Number(bomId),
                    input: {
                        name: 'New Options Section',
                        previous_substitute_section_id: null
                    }
                });
                if (newOptionSection?.data?.bod) {
                    setLoadingFlag(true);
                    try {
                        await updateData({
                            bomId: bomId!,
                            input: { id: newOptionSection?.data?.bod?.id, category: optionType, qty, product_to_replace: productToReplaceId }
                        });
                        setSnackbarMessage(addMsg);
                    } finally {
                        setLoadingFlag(false);
                    }
                } else {
                    const newSectionId = newOptionSection?.data?.id;
                    await handleAddOption({ type: optionType, qty, productToReplaceId, sectionId: newSectionId });
                }
            } else {
                setBodInfoForAddOption({ type: optionType, qty, productToReplaceId });
                setIsAddOptionModalOpen((prev) => !prev);
            }
        },
        [bomId, createOption, handleAddOption, noOfOptionSections, optionSection, updateData]
    );

    const checkIfProductsHasLots = useCallback(
        (ids: Array<number>) => {
            const idsToCheck = new Set(ids);
            const isLotPresentInBods = tableData?.some((rowData) => idsToCheck.has(rowData.id) && Boolean(lotMap[rowData.id])) || false;
            return isLotPresentInBods;
        },
        [lotMap, tableData]
    );

    //this is called when Change Section is called from menu options
    const onClickMoveSection = useCallback(
        (props: ICellRendererParams) => {
            if (!isOptionTable) {
                const isLotPresentInMovingBod = checkIfProductsHasLots([props.data.id]);
                setLotSelectedForMovingProducts(isLotPresentInMovingBod);
            }
            setMovingIds([props.data.id]);
            setCurrentSectionId(props?.data?.groupPath?.[0]);
            setOpenMoveDialog((old) => !old);
        },
        [checkIfProductsHasLots, isOptionTable]
    );

    const onClickDelete = useCallback((props: ICellRendererParams) => {
        setDeleteType('single');
        const { node, data } = props;
        if (node.level === ROW_LEVEL.SECTION) {
            setCurrentSectionId(data.groupPath[0]);
        } else if (node?.level === ROW_LEVEL.PRODUCT) {
            //this checks if the product/kit is the last item of section
            if (node?.parent?.childrenAfterGroup?.length === 1) {
                setCurrentSectionId(data.groupPath[0]);
            }
            setDeleteIds([data.id]);
        } else if (node?.level === ROW_LEVEL.COMPONENT) {
            //this checks if the component is the last item of kit
            if (node?.parent?.childrenAfterGroup?.length === 1) {
                setDeleteIds([data?.groupPath?.[1]]);
                if (node?.parent?.parent?.childrenAfterGroup?.length === 1) {
                    setCurrentSectionId(data?.groupPath?.[0]);
                }
            } else setDeleteIds([data.id]);
        }
        setOpenConfirmationDialog(true);
    }, []);

    const approveOption = useCallback(
        async (productId: number) => {
            await postApproveOption({ bomId: Number(bomId), data: { bod_id: [productId] } });
            setSnackbarMessage(OPTION_APPROVED_MSG);
        },
        [bomId, postApproveOption, setSnackbarMessage]
    );

    //this function is called when user click on Move to Kit option
    const onProductMoveToKit = useCallback(
        (currentBodId: number, sectionId: number) => {
            setCurrentSectionId(sectionId);
            setCurrentBodId(currentBodId);
            setOpenMoveProductDialog(true);
        },
        [setCurrentBodId, setCurrentSectionId, setOpenMoveProductDialog]
    );

    const onProductMoveOutFromKit = useCallback(
        async (params: ICellRendererParams) => {
            const { data, node } = params;
            let parentNode: IRowNode | null = null;
            if (node?.level === ROW_LEVEL.COMPONENT && !data.is_hidden && !node?.parent?.data?.is_hidden) {
                parentNode = node.parent;
            }
            await updateData({ bomId: String(bomId), input: { kit: null, id: data.id }, isInvalidateBodList: true });
            if (parentNode && parentNode?.allChildrenCount && parentNode?.allChildrenCount > 1) {
                const hiddenComponentsCount = parentNode?.childrenAfterGroup?.reduce((count, comp) => count + (comp?.data?.is_hidden ? 1 : 0), 0);

                if (hiddenComponentsCount && hiddenComponentsCount >= parentNode?.allChildrenCount - 1) {
                    await toggleHideData({
                        bomId: bomId,
                        input: [parentNode.data.id],
                        is_hidden: true
                    });

                    await onPricingInfoEdit({
                        discountedCost: null,
                        discount: null,
                        margin: null,
                        extendedDiscountCost: null,
                        extendedPrice: null,
                        cost: null,
                        id: parentNode.data.id,
                        sellPrice: null,
                        per: 1
                    });
                }
            }

            if (node.level === ROW_LEVEL.COMPONENT && parentNode?.allChildrenCount && parentNode.allChildrenCount > 1) {
                handleKitPricingUpdate({
                    onPricingInfoEdit,
                    tableRef,
                    type: 'component-move-out',
                    productIds: [data.id]
                });
            }
            //this checks if the component is the last item of kit
            if (node?.parent?.childrenAfterGroup?.length === 1) {
                await handleDeleteItems([], [data?.groupPath?.[1]]);
            }
            setSnackbarMessage(REMOVE_PRODUCT_FROM_KIT);
        },
        [bomId, handleDeleteItems, handleKitPricingUpdate, onPricingInfoEdit, tableRef, toggleHideData, updateData]
    );

    const onHighlight = useCallback(
        async (ids: number[]) => {
            await toggleHighLightData({
                bomId: bomId!,
                input: ids
            });
            setSnackbarMessage(HIGHLIGHT_MSG);
        },
        [bomId, setSnackbarMessage, toggleHighLightData]
    );

    const onCopyProductDataToKit = useCallback(
        async (props: ICellRendererParams) => {
            const { category, manufacturer, model_number, description, groupPath } = props.data || {};
            await updateData({
                bomId: bomId!,
                input: { id: groupPath[1], category, manufacturer, model_number, description }
            });
        },
        [bomId, updateData]
    );

    const onAddKitLine = useCallback(
        async (props: ICellRendererParams) => {
            const { data } = props;
            const { groupPath } = data || {};
            const item = {
                category: '',
                qty: null,
                manufacturer: '',
                model_number: '',
                previous_bod_id: null,
                section_id: groupPath?.[0],
                section_type: isOptionTable ? SECTION_TYPE.OPTION : SECTION_TYPE.MAIN,
                kit_id: groupPath[1]
            };
            await addData({ bomId: bomId, input: item });
            setSnackbarMessage(addMsg);
        },
        [addData, bomId, isOptionTable, setSnackbarMessage]
    );

    const onHide = useCallback(
        async (props: ICellRendererParams, hideStatus: boolean) => {
            let input: number[] = [];
            const { data, node } = props;
            let isHidden = false;

            if (node.level === ROW_LEVEL.SECTION && node.childrenAfterGroup) {
                // Map over the child nodes as well as leaf nodes if it exists to get their IDs
                input = node.childrenAfterGroup.reduce((acc: number[], childNode: IRowNode<any>) => {
                    if (childNode.allChildrenCount) {
                        childNode.allLeafChildren.forEach((item) => {
                            if (item.level !== childNode.level) {
                                acc.push(item.data.id);
                            }
                        });
                    }
                    acc.push(childNode.data.id);
                    return acc;
                }, []);

                isHidden = hideStatus;
            } else if (node.level === ROW_LEVEL.PRODUCT && node.allChildrenCount) {
                // hide/unhide kits and all of its component
                node.allLeafChildren.forEach((item) => {
                    input.push(Number(item.key));
                });
                isHidden = !hideStatus;
            } else {
                if (node.level === ROW_LEVEL.COMPONENT) {
                    const kit = node.parent?.data;
                    const parentNode = node?.parent;
                    const hiddenComponentsCount = parentNode?.childrenAfterGroup?.reduce((count, comp) => count + (comp?.data?.is_hidden ? 1 : 0), 0) || 0;

                    if ((kit.is_hidden && hideStatus) || (!kit.is_hidden && parentNode?.allChildrenCount && hiddenComponentsCount >= parentNode.allChildrenCount - 1)) {
                        input = [kit.id, data.id];
                    } else {
                        input = [data.id];
                    }
                } else {
                    input.push(data.id);
                }
                isHidden = !hideStatus;
            }

            try {
                await toggleHideData({
                    bomId: bomId,
                    input,
                    is_hidden: isHidden
                });
                if (node.level === ROW_LEVEL.COMPONENT || (node.level === ROW_LEVEL.PRODUCT && node.allChildrenCount)) {
                    handleKitPricingUpdate({
                        onPricingInfoEdit,
                        type: 'hide-unhide',
                        tableRef,
                        productIds: input,
                        isHidden
                    });
                }
                setSnackbarMessage(hideMsg);
            } catch (error) {
                setSnackbarMessage(someThingWentWrongMsg);
            }
        },
        [bomId, handleKitPricingUpdate, onPricingInfoEdit, tableRef, toggleHideData]
    );

    const hideKitsOnComponentsManipulate = useCallback(
        async (tableDataMap: Map<number, SectionAndBomItemData>, kitIds?: number[]) => {
            const currentKitIds = Array.isArray(kitIds) ? kitIds : partiallySelectedKitIds?.length ? partiallySelectedKitIds : [];
            if (currentKitIds.length === 0) return;
            const partialSelectedCompsFromKitMap = new Map(componentsFromPartialKits.map((item) => [item.id, item]));
            const input: number[] = [];

            // iterate though kits whose some components have been selected for deletion
            for (let kitId of currentKitIds) {
                const kitData = tableDataMap.get(kitId);
                // check if kit is not hidden
                if (!kitData?.is_hidden) {
                    let hideCount = 0,
                        componentLength = 0,
                        currentlySelectedComponents = partialSelectedCompsFromKitMap.size ? 0 : 1;
                    // by iterating thorugh tablesDataMap, get the size of kits component, total hidden components in the kit and total secected components in that kit which at the time of selection was not hidden.
                    for (let values of tableDataMap.values()) {
                        if (values.kit === kitId) {
                            componentLength++;
                            if (values.is_hidden) {
                                hideCount++;
                            } else if (partialSelectedCompsFromKitMap.has(values.id) && !values.is_hidden) {
                                currentlySelectedComponents++;
                            }
                        }
                    }
                    // If total hidden component + total selected unhidden components >= components's length, push that kit id to the inputs array
                    if (hideCount + currentlySelectedComponents >= componentLength) {
                        input.push(kitId);
                    }
                }
            }
            // if input aaray is not empty then hide those kits.
            if (input.length) {
                await toggleHideData({
                    bomId,
                    input,
                    is_hidden: true
                });

                tableRef?.current?.api?.forEachNode((node) => {
                    if (input.includes(node.data.id)) {
                        node.setData({ ...node.data, is_hidden: true });
                    }
                });

                // since kits are hidden, so kit cost will be set to null
                for (let kitId of input) {
                    try {
                        await onPricingInfoEdit({
                            discountedCost: null,
                            discount: null,
                            margin: null,
                            extendedDiscountCost: null,
                            extendedPrice: null,
                            cost: null,
                            id: kitId,
                            sellPrice: null,
                            per: 1
                        });
                    } catch (e) {
                        console.error(`Error while updating Kit cost to null for id : ${kitId}`);
                    }
                }
            }
        },
        [partiallySelectedKitIds, componentsFromPartialKits, toggleHideData, bomId, tableRef, onPricingInfoEdit]
    );

    const onHideMultiple = useCallback(async () => {
        const { selectedIds, status } = hideUnhideResult;
        const isHidden = status === 'hide' ? true : false;

        try {
            await toggleHideData({
                bomId: bomId,
                input: selectedIds,
                is_hidden: isHidden
            });

            if (partiallySelectedKitIds) {
                await hideKitsOnComponentsManipulate(hideUnhideResult.tableDataMap);
            }

            handleKitPricingUpdate({
                onPricingInfoEdit,
                type: 'hide-unhide',
                tableRef,
                productIds: selectedIds,
                isHidden
            });

            handleClearSelection();
            setSnackbarMessage(hideMsg);
        } catch (error) {
            console.error(`Error while hiding multiple kits: `, error);
            setSnackbarMessage(someThingWentWrongMsg);
        }
    }, [bomId, handleClearSelection, handleKitPricingUpdate, hideKitsOnComponentsManipulate, hideUnhideResult, onPricingInfoEdit, partiallySelectedKitIds, tableRef, toggleHideData]);

    //this function is called when user clicks on confirm from select option popup
    const handleAddOptionForMultipleOptions = useCallback(
        (sectionId: number) => {
            handleAddOption({ ...(bodInfoForAddOption || {}), sectionId });
        },
        [bodInfoForAddOption, handleAddOption]
    );

    //This is called when Change Section is called from bulk actions
    const onMoveMultipleItems = useCallback(() => {
        const sectionId = Number(Object.keys(hierarchy)[0]);
        setCurrentSectionId(sectionId);
        const ids = [...productIds, ...completelySelectedKitIdsFromCompleteSections, ...completelySelectedKitIdsFromPartialSections];
        const isLotPresentInMovingBods = checkIfProductsHasLots(ids);
        setLotSelectedForMovingProducts(isLotPresentInMovingBods);
        setMovingIds(ids);
        setOpenMoveDialog((old) => !old);
    }, [checkIfProductsHasLots, completelySelectedKitIdsFromCompleteSections, completelySelectedKitIdsFromPartialSections, hierarchy, productIds]);

    const updateCurrentSectionAndBodIds = useCallback((currentSectionId?: number, currentBodId?: number) => {
        if (currentSectionId) {
            setCurrentSectionId(currentSectionId);
        } else {
            setCurrentSectionId(undefined);
        }

        if (currentBodId) {
            setCurrentBodId(currentBodId);
        } else {
            setCurrentBodId(undefined);
        }
    }, []);

    const handleMoveProductDialog = useCallback(() => {
        setOpenMoveProductDialog(false);
        updateCurrentSectionAndBodIds(undefined, undefined);
    }, [updateCurrentSectionAndBodIds]);

    const rowDragTextCallback = useCallback(
        (params: IRowDragItem) => {
            const { rowNode } = params;
            const isSelectedRow = selectedItems.length === 0;
            const selectedItemsSummary = formatSelectedItemsText(rowNode);
            return isSelectedRow ? selectedItemsSummary : selectedText;
        },
        [selectedItems, selectedText]
    );

    const handleToggleAddOptionModal = useCallback(() => setIsAddOptionModalOpen((old) => !old), []);

    const onCellKeyDown = useCallback(
        (e: CellKeyDownEvent) => {
            const { event, node } = e;

            if (!event) return;
            const keyboardEvent = e.event as unknown as KeyboardEvent;
            const element = document.querySelector(AUTO_SELECT_CLASSESS);
            if (keyboardEvent.key === 'Enter') {
                if (element && getIsLastNodeInSectionHierarchy(node)) {
                    keyboardEvent.preventDefault();
                    return;
                } else if (getIsLastNodeInSectionHierarchy(node)) {
                    addNewRowInEndOfSection(e, onAddBelow);
                }
            }
            if ((keyboardEvent.ctrlKey || keyboardEvent.metaKey) && keyboardEvent.key === 'i') {
                onAddBelow(e);
            }
        },
        [addNewRowInEndOfSection, onAddBelow]
    );

    const handleOnGridReady = useCallback((params: any) => {
        const { leftWidth, rightWidth, viewportRight } = params?.api?.columnModel || {};
        const maxViewPortWidth = leftWidth + rightWidth + viewportRight;
        // divided by 3 width will equally distributed across type,manufacture,modelNo
        const maxPinnedColumnsWidth = Math.floor((maxViewPortWidth - PINNED_AND_FIX_COLUMN_WIDTH) / 3);
        setMaxWidthForPinnedColumn(maxPinnedColumnsWidth);
        setGridReady(true);
    }, []);

    const onCloseBanner = useCallback(() => {
        setSelectedItems([]);
        handleClearSelection();
    }, [handleClearSelection]);

    const onHighlightMultiple = useCallback(async () => {
        onHighlight(highlightResult.selectedIds);
        handleClearSelection();
    }, [handleClearSelection, highlightResult.selectedIds, onHighlight]);

    const calculateTableHeight = useCallback(() => {
        if (tableRef.current && tableData.length) {
            const visibleRowCount = tableRef.current.api.getDisplayedRowCount();
            const height = getTableHeight({ rowHeight: ROW_HEIGHT, rows: visibleRowCount || 0, tableMaxHeight: tableMaxHeight || 0, hasOption: true });
            setTableHeight(height);
        } else {
            setTableHeight(317);
        }
    }, [tableRef, tableData.length, tableMaxHeight]);

    const onRowGroupOpened = useCallback(
        (e: RowGroupOpenedEvent) => {
            const {
                rowIndex,
                node: { expanded, id },
                event
            } = e;
            if (event) {
                // Added it becasue on Row Drag it called
                if (rowIndex !== null) {
                    if (expanded) {
                        expandedRowsContainerRef.current.push(rowIndex);
                    } else {
                        expandedRowsContainerRef.current.pop();
                    }
                }

                const obj = { ...JSON.parse(localStorage.getItem(`${bomId}-kits`) || '{}'), [`${id}`]: expanded };
                setKitsExpansionObj?.(obj);
                localStorage.setItem(`${bomId}-kits`, JSON.stringify(obj));
            }
            calculateTableHeight();
        },
        [bomId, calculateTableHeight]
    );

    const toggleKits = useCallback(
        (params: FirstDataRenderedEvent) => {
            if (kitsExpansionObj && Object.keys(kitsExpansionObj).length > 0) {
                Object.keys(kitsExpansionObj).forEach((id: string) => {
                    const rowNode = params.api.getRowNode(id);
                    if (rowNode) {
                        rowNode.setExpanded(kitsExpansionObj?.[id]);
                    }
                });
            }
            calculateTableHeight();
        },
        [calculateTableHeight, kitsExpansionObj]
    );

    const onDuplicateMultiple = useCallback(async () => {
        const input = [...productIds, ...completelySelectedKitIdsFromCompleteSections, ...completelySelectedKitIdsFromPartialSections, ...componentIdsFromPartialKits];
        try {
            await duplicateData({
                bomId,
                input
            });

            if (componentsFromPartialKits?.length) {
                handleKitPricingUpdate({
                    onPricingInfoEdit,
                    tableRef,
                    type: 'duplicate',
                    componentsFromPartialKits
                });
            }

            handleClearSelection();
            setSnackbarMessage(duplicateMsg);
        } catch (e) {
            setSnackbarMessage(someThingWentWrongMsg);
        }
    }, [
        bomId,
        completelySelectedKitIdsFromCompleteSections,
        completelySelectedKitIdsFromPartialSections,
        componentIdsFromPartialKits,
        componentsFromPartialKits,
        duplicateData,
        handleClearSelection,
        handleKitPricingUpdate,
        onPricingInfoEdit,
        productIds,
        tableRef
    ]);

    const handleOnRowDataUpdated = useCallback(() => calculateTableHeight(), [calculateTableHeight]);

    const closeConfirmationDialog = useCallback(() => {
        setOpenConfirmationDialog(false);
        setCurrentSectionId(undefined);
        setDeleteType('');
        setDeleteIds([]);
    }, []);

    const onClickMultipleDelete = useCallback(() => {
        setDeleteType('bulk');
        setOpenConfirmationDialog(true);
    }, []);

    const onDeleteBulk = useCallback(async () => {
        try {
            const isBulkDelete = deleteType === 'bulk';
            const productIds = isBulkDelete ? [...productsIdsFromPartialSections, ...componentIdsFromPartialKits, ...completelySelectedKitIdsFromPartialSections] : deleteIds;
            const sectionIds = isBulkDelete ? completelySelectedSectionIds : currentSectionId ? [currentSectionId] : [];
            await handleDeleteItems(sectionIds, productIds, componentsFromPartialKits);
            if (componentsFromPartialKits.length) {
                hideKitsOnComponentsManipulate(hideUnhideResult.tableDataMap);
            } else if (productIds.length === 1) {
                const tableDataMap = new Map();
                tableRef?.current?.api.forEachNode(({ data }) => {
                    if (data?.groupPath?.length > 1) {
                        const { is_hidden, groupPath, id, is_kit, kit } = data;
                        tableDataMap.set(data.id, { is_hidden, groupPath, id, is_kit, kit });
                    }
                });
                const kitId = tableDataMap.get(productIds[0])?.kit;
                if (kitId) {
                    hideKitsOnComponentsManipulate(tableDataMap, [kitId]);
                }
            }

            closeConfirmationDialog();
            handleClearSelection();
            setSnackbarMessage(deleteMsg);
        } catch (error) {
            setSnackbarMessage(someThingWentWrongMsg);
        }
    }, [
        closeConfirmationDialog,
        completelySelectedKitIdsFromPartialSections,
        completelySelectedSectionIds,
        componentIdsFromPartialKits,
        componentsFromPartialKits,
        currentSectionId,
        deleteIds,
        deleteType,
        handleClearSelection,
        handleDeleteItems,
        hideKitsOnComponentsManipulate,
        hideUnhideResult.tableDataMap,
        productsIdsFromPartialSections,
        tableRef
    ]);

    const toggleMoveSectionDialog = useCallback(
        (afterSubmission?: boolean) => {
            if (afterSubmission) {
                handleClearSelection();
            }
            setOpenMoveDialog((old) => !old);
            setMovingIds([]);
            setCurrentSectionId(undefined);
        },
        [handleClearSelection]
    );

    const handleMoveSection = useCallback(
        async (previousSectionId = 0, currentSectionIds: number[] = []) => {
            let payload: UpdateSectionOrderTypes = {
                bomId
            };

            if (!isOptionTable) {
                payload = { ...payload, sectionType: SECTION_TYPE_FOR_PAYLOAD.MAIN, previous_section_id: previousSectionId, section_id: currentSectionIds };
            } else {
                payload = {
                    ...payload,
                    sectionType: SECTION_TYPE_FOR_PAYLOAD.OPTION,
                    previous_substitute_section_id: previousSectionId,
                    substitute_section_id: currentSectionIds
                };
            }
            await reOrderSectionMutation(payload);
        },
        [bomId, isOptionTable, reOrderSectionMutation]
    );

    const onRowDragMove = useCallback(
        (event: RowDragMoveEvent) => {
            const { overNode, node, nodes, api } = event;
            if (node?.level === ROW_LEVEL.SECTION) {
                event.api.collapseAll();
            }
            // handle it kit is started moving collaps all the kits
            if (completelySelectedKitIdsFromPartialSections.length > 0 || (node.level === ROW_LEVEL.PRODUCT && node.data.is_kit)) {
                api.forEachNode((node) => {
                    if (node.data.is_kit) {
                        node.setExpanded(false);
                    }
                });
            }

            if (!dragInitialParentNodeRef.current && nodes.length === 1) {
                if (node?.level === ROW_LEVEL.PRODUCT) {
                    if (node?.parent?.childrenAfterGroup?.length === 1) {
                        dragInitialParentNodeRef.current = { id: node.data.groupPath[0], isProduct: true };
                    }
                } else if (node?.level === ROW_LEVEL.COMPONENT) {
                    if (node?.parent?.childrenAfterGroup?.length === 1) {
                        dragInitialParentNodeRef.current = { id: node.data.groupPath[1], isKit: true };
                        if (node?.parent?.parent?.childrenAfterGroup?.length === 1) {
                            dragInitialParentNodeRef.current = { id: node.data.groupPath[0], isProduct: true };
                        }
                    }
                }
            }

            setPotentialParentRowDragAndDropNode(event.api, overNode, potentialParentRef, currentDropNodeRef, event?.y);
        },
        [completelySelectedKitIdsFromPartialSections.length]
    );

    const onRowDragLeave = useCallback((event: RowDragLeaveEvent) => {
        setPotentialParentRowDragAndDropNode(event.api, null, potentialParentRef, currentDropNodeRef, event?.y);
    }, []);

    const handleKitHideUnhideWhileRowDrag = useCallback(
        async ({ productIds, isDragOverKit, prevProductKitId }: { productIds: number[]; isDragOverKit: Boolean; prevProductKitId?: number }) => {
            // when doing drag drop over one product
            if (productIds.length === 1) {
                const tableDataMap = new Map();

                tableRef?.current?.api.forEachNode((rowNode) => {
                    const { data } = rowNode;
                    if (data?.groupPath?.length > 1) {
                        const { is_hidden, groupPath, id, is_kit, kit } = data;
                        tableDataMap.set(data.id, { is_hidden, groupPath, id, is_kit, kit });
                    }
                });

                const product = tableDataMap.get(productIds[0]);
                const kit = isDragOverKit ? tableDataMap.get(isDragOverKit) : product?.kit ? tableDataMap.get(product.kit) : null;
                if (product) {
                    // when moving product or component (from another kit) to destination kit
                    if (isDragOverKit && kit) {
                        let isHidden: boolean | null = null;
                        if (kit?.is_hidden && !product?.is_hidden) {
                            isHidden = true;
                        } else if (!kit?.is_hidden && product?.is_hidden) {
                            isHidden = false;
                        }

                        if (isHidden != null && product?.id) {
                            await toggleHideData({
                                bomId: bomId,
                                input: [product.id],
                                is_hidden: isHidden
                            });

                            // setting the rowNode state such that when kit cost is being updated it has the updated rowNode data
                            const rowNode = tableRef?.current?.api.getRowNode(String(product.id));
                            if (rowNode) {
                                rowNode.setData({ ...rowNode.data, is_hidden: isHidden });
                            }
                        }

                        // if draggable source has kit, then check if source kit needs to be hidden or not
                        if (prevProductKitId) {
                            await hideKitsOnComponentsManipulate(tableDataMap, [prevProductKitId]);
                        }
                    } else if (kit && !kit.is_hidden && !product.is_hidden && prevProductKitId) {
                        // when moving component out of un hidden kit having hide count >= length of kit's component, then hide the kit and set the kit cost to null
                        await hideKitsOnComponentsManipulate(tableDataMap, [prevProductKitId]);
                    }
                }
            }
            // when we are doing drag drop over multiple products
            else if (productIds.length > 1) {
                const { tableDataMap } = hideUnhideResult;
                if (isDragOverKit) {
                    // This means we are moving the selected items into the kit and hide/unhide state of the kit will matter here.
                    const kit = tableDataMap.get(isDragOverKit);
                    let isHidden: boolean | null = null;
                    let inputIds: number[] = [];

                    // since kit is hidden, therefore, hiding the draggable products without updating the kit cost
                    if (kit && kit.is_hidden) {
                        inputIds = productIds;
                        isHidden = true;
                    } else if (kit && !kit.is_hidden) {
                        // since kit is not hidden, draggable products will acquire the hide/unhide state of kit
                        productIds.forEach((id) => {
                            if (tableDataMap.get(id).is_hidden) {
                                inputIds.push(id);
                            }
                        });
                        isHidden = false;
                    }
                    if (inputIds.length && isHidden != null) {
                        await toggleHideData({
                            bomId: bomId,
                            input: inputIds,
                            is_hidden: isHidden
                        });

                        tableRef?.current?.api?.forEachNode((node) => {
                            if (inputIds.includes(node.data.id)) {
                                node.setData({ ...node.data, is_hidden: isHidden });
                            }
                        });
                    }
                }
                await hideKitsOnComponentsManipulate(tableDataMap);
            }
        },
        [tableRef, toggleHideData, bomId, hideKitsOnComponentsManipulate, hideUnhideResult]
    );

    const onRowDragEnd = useCallback(
        async (event: RowDragEndEvent) => {
            const { overNode, nodes } = event;
            setPotentialParentRowDragAndDropNode(event.api, null, potentialParentRef, currentDropNodeRef, event?.y);

            if (kitsExpansionObj && Object.keys(kitsExpansionObj).length > 0) {
                Object.keys(kitsExpansionObj).forEach((id) => {
                    const rowNode = event.api.getRowNode(id);
                    if (rowNode) {
                        rowNode.setExpanded(kitsExpansionObj?.[id]);
                    }
                });
            }

            // if drag start and drow in selected area so avoid api call.
            if (overNode?.isSelected() && nodes.every((node) => node.isSelected())) {
                return;
            }

            if (!overNode || nodes.filter((node) => !node.group).length === 0) return;

            const isDragOverKit = overNode?.data.is_kit ? overNode.data.id : overNode?.data.kit;
            const selectedRow = selectedItems.length === 0 ? [nodes[0].data] : selectedItems;

            const selectedNodesDoesContainSection = nodes.some((item) => item?.level === ROW_LEVEL.SECTION);
            if (overNode?.level === ROW_LEVEL.SECTION && selectedNodesDoesContainSection) {
                const currentSectionIds: number[] = nodes
                    .filter((item) => item?.level === 0)
                    .sort((a, b) => (a?.rowIndex ?? 0) - (b?.rowIndex ?? 0))
                    .map((item) => item.data.id);
                const checkDropAtFirstNode = event.y < ROW_HEIGHT / 2;
                const previousSectionId = checkDropAtFirstNode ? null : overNode.data.id;
                handleMoveSection(previousSectionId, currentSectionIds);
                return;
            }
            try {
                const payload = {
                    section_type: isOptionTable ? SECTION_TYPE.OPTION : SECTION_TYPE.MAIN,
                    section_id: overNode.data.section || overNode?.data.substitute_section,
                    kit_id: isDragOverKit ?? null,
                    previous_bod_id: overNode.level === ROW_LEVEL.SECTION ? null : overNode.data.id,
                    bod_id:
                        selectedItems.length === ROW_LEVEL.SECTION
                            ? [selectedRow?.[0]?.id]
                            : [...productIds, ...completelySelectedKitIdsFromPartialSections, ...completelySelectedKitIdsFromCompleteSections, ...componentIdsFromPartialKits]
                };

                await reorderNestingData({ bomId: bomId!, input: payload });
                if (dragInitialParentNodeRef.current && selectedItems.length < 1) {
                    if (dragInitialParentNodeRef.current.isProduct) {
                        await handleDeleteItems([dragInitialParentNodeRef.current.id], []);
                    } else {
                        await handleDeleteItems([], [dragInitialParentNodeRef.current.id]);
                    }
                    dragInitialParentNodeRef.current = null;
                    return;
                } else {
                    // Replace the existing code with this:
                    const selectedSectionIds = getSelectedSectionIds(hierarchy);
                    if (selectedSectionIds.length) {
                        await handleDeleteItems(selectedSectionIds, []); // Delete section andall the item moved out.
                        return;
                    }
                }
                setSnackbarMessage(reorderMsg);
                await handleKitHideUnhideWhileRowDrag({
                    productIds: payload.bod_id,
                    isDragOverKit,
                    prevProductKitId: selectedRow.length === 1 && selectedRow[0]?.kit ? selectedRow[0].kit : undefined
                });

                handleKitPricingUpdate({
                    onPricingInfoEdit,
                    tableRef,
                    type: 'drag-drop',
                    rowDragPayload: payload
                });
            } catch (error: any) {
                console.error(`Error while performing row drag : `, error);
                setSnackbarMessage(error?.status === 400 ? error.response.data.message : someThingWentWrongMsg);
            }
        },
        [
            kitsExpansionObj,
            selectedItems,
            handleMoveSection,
            isOptionTable,
            productIds,
            completelySelectedKitIdsFromPartialSections,
            completelySelectedKitIdsFromCompleteSections,
            componentIdsFromPartialKits,
            reorderNestingData,
            bomId,
            handleKitHideUnhideWhileRowDrag,
            handleKitPricingUpdate,
            onPricingInfoEdit,
            tableRef,
            handleDeleteItems,
            hierarchy
        ]
    );

    const onCheckboxChange = useCallback(
        (params: SelectionChangedEvent) => {
            const selectedBods = params.api.getSelectedRows() as SelectedData[];
            if (selectedBods.length) {
                setSelectedItems(selectedBods);
                aggregateSelectedItemsFromSections?.(selectedBods);
            } else {
                setSelectedItems([]);
            }
        },
        [aggregateSelectedItemsFromSections]
    );

    const handleSectionNameUpdate = useCallback(
        async (props: any) => {
            const { newValue, data, node } = props;
            if (!newValue?.trim()) {
                node.setData({ ...data, category: data.name });
                return setSnackbarMessage(SECTION_NAME_EMPTY_MSG);
            }
            await onEditSectionName(data.id, newValue, Boolean(isOptionTable));
            setSnackbarMessage(SECTION_UPDATE_MSG);
        },
        [isOptionTable, onEditSectionName]
    );

    const onHandleUnpinnedColumns = useCallback((props: ProcessUnpinnedColumnsParams) => {
        // divided by 3 width will equally distributed across type,manufacture,modelNo
        const maxColumnsWidth = Math.floor((props?.viewportWidth - PINNED_AND_FIX_COLUMN_WIDTH) / 3);
        setMaxWidthForPinnedColumn(maxColumnsWidth);
        return [];
    }, []);

    const handleOnPasteEnd = useCallback(() => {
        currentGroupWhilePastingRef.current = null;
        if (startColumnOnPasteRef?.current != null) {
            let removeColsArray: string[] = [];
            if (currentTabValue === TAB_VALUES.PRICING_AND_LEAD_TIME && !isOptionTable) {
                removeColsArray = [BOM_TABLES_ENUM.ADD_DEDUCT_AMT, BOM_TABLES_ENUM.PRODUCT_TO_REPLACE];
            } else if (currentTabValue === TAB_VALUES.PRICING_AND_LEAD_TIME && isOptionTable) {
                removeColsArray = [BOM_TABLES_ENUM.LOT];
            }
            addNewRowsToTheTable(selectedRowsRef, tableRef, startColumnOnPasteRef.current, createNewRowsFromClipboard, removeColsArray);
            startColumnOnPasteRef.current = undefined;
        }
    }, [currentGroupWhilePastingRef, currentTabValue, isOptionTable, selectedRowsRef, tableRef, createNewRowsFromClipboard]);

    const onCreateSection = useCallback(
        async (onSectionCreateSuccess?: () => void) => {
            const type = isOptionTable ? 'options' : 'products';
            await handleCreateSection(type, onSectionCreateSuccess);
            handleOnPasteEnd();
            setSnackbarMessage(isOptionTable ? OPTION_CREATE_MSG : SECTION_CREATE_MSG);
        },
        [handleCreateSection, handleOnPasteEnd, isOptionTable]
    );

    const onCreateSectionForEmptyTable = useCallback(() => onCreateSection(focusOnFirstSectionCell), [focusOnFirstSectionCell, onCreateSection]);

    const updateKitCreationError = useCallback((error: KIT_ERROR_TYPE.KIT | KIT_ERROR_TYPE.OPTION | undefined) => {
        if (!error) {
            setKitCreationError(undefined);
        } else {
            setKitCreationError(error);
        }
    }, []);

    const handleToggleSectionModal = useCallback(() => setShowSectionModal((old) => !old), []);

    const onCreateKitWithoutLineItems = useCallback(
        async (sectionId: number) => {
            const inputData = {
                bomId: Number(bomId),
                input: {
                    category: '',
                    qty: null,
                    manufacturer: '',
                    model_number: '',
                    previous_bod_id: null,
                    section_id: sectionId,
                    components: [],
                    section_type: isOptionTable ? SECTION_TYPE.OPTION : SECTION_TYPE.MAIN
                }
            };
            await createKit(inputData);
            setSnackbarMessage(KIT_CREATED_SUCCESS);
            handleToggleSectionModal();
        },
        [bomId, createKit, handleToggleSectionModal, isOptionTable]
    );

    const onCreateKitWithSelectedLineItems = useCallback(async () => {
        if (!selectedItems.length) {
            return handleToggleSectionModal();
        }
        if (!isOptionTable) {
            let hasOptions = false;
            selectedItems.forEach((item) => {
                if (mainProductOptionsInfo?.[item.id]?.length) {
                    hasOptions = true;
                }
            });
            if (hasOptions) {
                return updateKitCreationError(KIT_ERROR_TYPE.OPTION);
            }
        }
        const id = tableData?.length ? tableData[tableData!.length - 1].id : null;
        const sectionId = Number(Object.keys(hierarchy || {})?.[0]);
        const inputData = {
            bomId: Number(bomId),
            input: {
                category: '',
                qty: null,
                manufacturer: '',
                model_number: '',
                previous_bod_id: id,
                section_id: Number(sectionId),
                components: productIds,
                section_type: isOptionTable ? SECTION_TYPE.OPTION : SECTION_TYPE.MAIN
            }
        };
        await createKit(inputData);
        setSnackbarMessage(KIT_CREATED_SUCCESS);
        handleClearSelection();
    }, [bomId, createKit, handleClearSelection, handleToggleSectionModal, hierarchy, isOptionTable, mainProductOptionsInfo, productIds, selectedItems, tableData, updateKitCreationError]);

    const handleRangeSelectionChanged = useCallback(
        (params: RangeSelectionChangedEvent) => {
            handleOnRangeSelectionChanged(params, tableRef, selectedRowsRef, currentGroupWhilePastingRef);
        },
        [currentGroupWhilePastingRef, selectedRowsRef, tableRef]
    );

    const getTableMetaData = useCallback(() => {
        if (!tableRef?.current) return null;
        return tableRef.current;
    }, [tableRef]);

    const handleProductToReplaceChange = useCallback(
        async (productToReplaceId: number | null, bodId: number) => {
            await updateData({
                bomId: bomId,
                input: { id: bodId, product_to_replace: productToReplaceId }
            });
        },
        [bomId, updateData]
    );

    const onCellContextMenu = useCallback(async (props: CellContextMenuEvent) => {
        handleRightClickContextMenuChange(props);

        // this is added so that clear section to work fine with right click
        clearCheckBoxSelectionFromAPI(props);

        setContextMenu(true);
    }, []);

    const onCellFocused = useCallback(
        (props: CellFocusedEvent) => {
            const api = props.api as any;
            const isContextMenuPopupOpen = api.contextMenuFactory.activeMenu;

            if (!isContextMenuPopupOpen && isContextMenuOpen) {
                setSelectedItems([]);
                setContextMenu(false);
            }
        },
        [isContextMenuOpen]
    );

    const suppressKeyboardEvent = useCallback((params: SuppressKeyboardEventParams): boolean => {
        const isEnter = params.event.key === 'Enter';
        const isManufacturerColumn = params.column.getColId() === BOM_TABLES_ENUM.MANUFACTURER;
        const muiAutocompleteElement = document.querySelector('.Mui-focusVisible.MuiTypography-root.MuiAutocomplete-option');
        return isEnter && isManufacturerColumn && !!muiAutocompleteElement;
    }, []);

    const valueGetterForMfg = useCallback(
        (params: ValueGetterParams) => {
            return getFormattedManufacturerName(companyMfgData, params.data.manufacturer);
        },
        [companyMfgData]
    );

    const handleKitPricingCallback = useCallback(
        (type: OperationType, bodId?: number, payload?: BulkEditPayload[]) => {
            function updateKitPricing(onPricingInfoEdit: (info: PricingDataType) => Promise<void>, tableRef: React.RefObject<AgGridReact<any>>) {
                return handleKitPricingUpdate({
                    onPricingInfoEdit,
                    tableRef,
                    type,
                    productIds: bodId ? [bodId] : undefined,
                    bulkEditPayload: payload ? payload : undefined
                });
            }
            return updateKitPricing(onPricingInfoEdit, tableRef);
        },
        [handleKitPricingUpdate, onPricingInfoEdit, tableRef]
    );

    const handleBulkEdit = useCallback(() => {
        setOpenBulkEditModal((prev) => !prev);
    }, []);

    return {
        onAddKitLine,
        onCopyProductDataToKit,
        onHighlight,
        onProductMoveOutFromKit,
        onProductMoveToKit,
        approveOption,
        onClickDelete,
        onClickMoveSection,
        onAddOption,
        onDuplicate,
        onAddBelow,
        handleAddOptionForMultipleOptions,
        setSnackbarMessage,
        onMoveMultipleItems,
        onHide,
        onHideMultiple,
        handleMoveProductDialog,
        rowDragTextCallback,
        handleToggleAddOptionModal,
        onCellKeyDown,
        handleOnGridReady,
        onCloseBanner,
        onHighlightMultiple,
        onRowGroupOpened,
        toggleKits,
        setKitsExpansionObj,
        onDuplicateMultiple,
        handleOnRowDataUpdated,
        closeConfirmationDialog,
        onClickMultipleDelete,
        onDeleteBulk,
        toggleMoveSectionDialog,
        onRowDragEnd,
        onRowDragMove,
        onRowDragLeave,
        onCheckboxChange,
        handleSectionNameUpdate,
        onHandleUnpinnedColumns,
        onCreateSection,
        onCreateSectionForEmptyTable,
        handleClearSelection,
        onCreateKitWithoutLineItems,
        onCreateKitWithSelectedLineItems,
        updateKitCreationError,
        handleToggleSectionModal,
        handleRangeSelectionChanged,
        handleOnPasteEnd,
        handleSearch,
        getTableMetaData,
        updateCurrentSectionAndBodIds,
        handleProductToReplaceChange,
        onCellContextMenu,
        onCellFocused,
        setSelectedItems,
        addNewRowsToTheTable,
        createNewRowsFromClipboard,
        suppressKeyboardEvent,
        valueGetterForMfg,
        handleKitPricingCallback,
        isHoverCellClassRules,
        lotMap,
        isTableLoadingAfterMutation,
        snackbarMessage,
        isAddOptionModalOpen,
        loadingFlag,
        bodInfoForAddOption,
        isLotSelectedForMovingProducts,
        movingIds,
        currentSectionId,
        openMoveDialog,
        deleteType,
        deleteIds,
        openConfirmationDialog,
        openMoveProductDialog,
        currentBodId,
        hierarchy,
        addDataLoading,
        isCreateKitDisabled,
        isChangeSectionDisabled,
        selectedText,
        selectedItems,
        productIds,
        isSectionSelected,
        gridReady,
        tableHeight,
        deleteLoading,
        maxWidthForPinnedColumn,
        kitCreationError,
        showSectionModal,
        createKitLoading,
        quickFilterText,
        noOfOptionSections,
        autoGroupColumnDef,
        bulkActionsHideUnhideStatus: hideUnhideResult.status,
        bulkActionHighLightStatus: highlightResult.status,
        startColumnOnPasteRef,
        companyMfgData,
        openBulkEditModal,
        handleBulkEdit,
        bomRoundingPreset
    };
};
